"use client";

import React, { useState, useEffect } from "react";
import {
  formatDateStringdot,
  formatBookingId,
} from "../../CommonJquery/CommonJquery";
import Loader from "../Loader";

const ViewServiceCharge = ({ tollChargesData }) => {
  const tollDetails = tollChargesData;
  const [showLoaderAdmin, setshowLoaderAdmin] = useState(false);
  const [detailedServiceCharges, setDetailedServiceCharges] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    console.log("ViewServiceCharge tollDetails:", tollDetails); // Debug log
    if (tollDetails) {
      const detailsArray = Array.isArray(tollDetails)
        ? tollDetails
        : [tollDetails];
      setDetailedServiceCharges(detailsArray);
      console.log("Set detailedServiceCharges:", detailsArray); // Debug log
    } else {
      setDetailedServiceCharges([]);
      console.log("No tollDetails, set empty array"); // Debug log
    }
  }, [tollDetails]);

  const handlePaymentType = (type) => {
    const lowerType = type || "";
    switch (lowerType.toLowerCase()) {
      case "cash":
        return {
          type: "Cash",
          icon: "bi-cash",
          class: "accounting-cash",
        };
      case "stripe":
        return {
          type: "Stripe",
          icon: "bi-credit-card",
          class: "accounting-stripe",
        };
      case "zelle":
        return {
          type: "Zelle",
          icon: "bi-currency-dollar",
          class: "accounting-zelle",
        };
      case "card":
        return {
          type: "Card",
          icon: "bi-phone",
          class: "accounting-upi",
        };
      default:
        return {
          type: "Unknown",
          icon: "bi-question-circle",
          class: "accounting-unknown",
        };
    }
  };

  // Prepare table data
  const tableData = detailedServiceCharges.map((entry) => {
    const details = [];
    if (entry.toll_charge)
      details.push(`Toll: $${Number(entry.toll_charge).toFixed(2)}`);
    if (entry.damage_charge)
      details.push(`Damage: $${Number(entry.damage_charge).toFixed(2)}`);
    if (entry.ticket_charge)
      details.push(`Ticket: $${Number(entry.ticket_charge).toFixed(2)}`);
    if (entry.other_charge)
      details.push(`Other: $${Number(entry.other_charge).toFixed(2)}`);

    const total =
      (Number(entry.toll_charge) || 0) +
      (Number(entry.damage_charge) || 0) +
      (Number(entry.ticket_charge) || 0) +
      (Number(entry.other_charge) || 0);

    return {
      date: entry.entry_date,
      entry_date: entry.entry_date,
      affected_date: entry.service_date,
      booking_id: formatBookingId(entry.booking_id) || "-",
      description: `${entry.notes || "Service Charges"} (${details.join(
        ", "
      )})`,
      debit: total,
      credit: null,
      payment_mode: "-",
      source: entry.entry_from === 1 ? "Driver" : "Admin",
      type: "Charge",
      transaction_id: entry.primary_id?.toString() ?? "-",
    };
  });

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = tableData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(tableData.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const renderPaginationButtons = () => {
    const buttons = [];

    buttons.push(
      <li className="cust_hist_page_item" key="prev">
        <button
          className="cust_hist_page_link cust_hist_page_prev"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <span className="sr-only">Previous</span>
          <svg
            className="cust_hist_page_icon"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </li>
    );

    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + 4);

    if (endPage === totalPages) {
      startPage = Math.max(1, endPage - 4);
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <li className="cust_hist_page_item" key={i}>
          <button
            className={`cust_hist_page_link ${
              currentPage === i ? "cust_hist_page_active" : ""
            }`}
            onClick={() => handlePageChange(i)}
          >
            {i}
          </button>
        </li>
      );
    }

    buttons.push(
      <li className="cust_hist_page_item" key="next">
        <button
          className="cust_hist_page_link cust_hist_page_next"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages || totalPages === 0}
        >
          <span className="sr-only">Next</span>
          <svg
            className="cust_hist_page_icon"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </li>
    );

    return buttons;
  };

  return (
    <>
      {showLoaderAdmin && <Loader />}
      {tableData.length === 0 ? (
        <div className="p-3 text-center text-muted">
          No service charge details available
        </div>
      ) : (
        <div className="accounting-table-container">
          <table className="accounting-table">
            <thead>
              <tr>
                <th style={{ whiteSpace: "nowrap" }}>Entry Date</th>
                <th style={{ whiteSpace: "nowrap" }}>Effective Date</th>
                <th style={{ whiteSpace: "nowrap" }}>Booking ID</th>
                <th style={{ whiteSpace: "nowrap" }}>Description</th>
                <th style={{ whiteSpace: "nowrap" }}>Debit ($)</th>
                <th style={{ whiteSpace: "nowrap" }}>Credit ($)</th>
                <th style={{ whiteSpace: "nowrap" }}>Payment Mode</th>
                <th style={{ whiteSpace: "nowrap" }}>Source</th>
                <th style={{ whiteSpace: "nowrap" }}>Type</th>
                <th style={{ whiteSpace: "nowrap" }}>Transaction ID</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map((transaction, index) => (
                <tr key={index}>
                  <td>{formatDateStringdot(transaction.date)}</td>
                  <td>{formatDateStringdot(transaction.affected_date)}</td>
                  <td>
                    {transaction.booking_id !== "-" && "#"}
                    {transaction.booking_id}
                  </td>
                  <td>{transaction.description}</td>
                  <td style={{ color: "red" }}>
                    {transaction?.debit?.toFixed(2) || "-"}
                  </td>
                  <td style={{ color: "green" }}>
                    {transaction?.credit?.toFixed(2) || "-"}
                  </td>
                  <td>
                    <div
                      className={` ${
                        transaction?.payment_mode !== "-"
                          ? "accounting-payment-mode"
                          : "accounting-payment-mode2"
                      } ${handlePaymentType(transaction.payment_mode).class}`}
                    >
                      {transaction?.payment_mode !== "-"
                        ? handlePaymentType(transaction.payment_mode).type
                        : transaction?.payment_mode}
                    </div>
                  </td>
                  <td>
                    <span
                      className={`accounting-status accounting-status-${
                        transaction.source === "Driver"
                          ? "credit_user"
                          : "credit_admin"
                      }`}
                    >
                      {transaction.source}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`accounting-status accounting-status-${
                        transaction.debit === "" || transaction.debit == null
                          ? "credit"
                          : "debit"
                      }`}
                    >
                      {transaction.type}
                    </span>
                  </td>
                  <td>
                    {transaction.transaction_id
                      ? transaction.transaction_id.slice(0, 15) + "..."
                      : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="cust_hist_card_footer">
        <div className="cust_hist_pagination_info">
          {tableData.length > 0 ? (
            <>
              Showing{" "}
              <span className="cust_hist_pagination_bold">
                {indexOfFirstItem + 1}
              </span>{" "}
              to{" "}
              <span className="cust_hist_pagination_bold">
                {Math.min(indexOfLastItem, tableData.length)}
              </span>{" "}
              of{" "}
              <span className="cust_hist_pagination_bold">
                {tableData.length}
              </span>{" "}
              service charges
            </>
          ) : (
            <>
              Showing <span className="cust_hist_pagination_bold">0</span>{" "}
              service charges
            </>
          )}
        </div>
        {tableData.length > 0 && (
          <div className="cust_hist_pagination">
            <nav aria-label="Pagination">
              <ul className="cust_hist_pagination_list">
                {renderPaginationButtons()}
              </ul>
            </nav>
          </div>
        )}
      </div>
    </>
  );
};

export default ViewServiceCharge;
